/*
 * Copyright (C) 2025 Hendrik. All Rights Reserved.
 */

import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import './ResourceBank.css';

// --- SVG Icons ---
const Icons = {
    Upload: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>,
    File: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path><polyline points="13 2 13 9 20 9"></polyline></svg>,
    Download: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>,
    Trash: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>,
    Filter: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>,
    Folder: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path></svg>
};

const ResourceBank = () => {
    const [resources, setResources] = useState([]);
    const [userRole, setUserRole] = useState('');
    const [filters, setFilters] = useState({ grade: '', subject: '' });
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');

    // Form Refs
    const titleRef = useRef();
    const gradeRef = useRef();
    const subjectRef = useRef();
    const fileRef = useRef();

    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost/api';

    useEffect(() => {
        const role = localStorage.getItem('user_role') || '';
        setUserRole(role);
        fetchResources();
    }, []);

    const fetchResources = async () => {
        try {
            const token = localStorage.getItem('auth_token');
            const res = await axios.get(`${API_URL}/fetch_library.php`, {
                params: filters,
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setResources(res.data.resources || []);
        } catch (err) {
            setResources([]);
        }
    };

    const handleFilterChange = (e) => {
        setFilters({ ...filters, [e.target.name]: e.target.value });
    };

    const handleFilterSubmit = (e) => {
        e.preventDefault();
        fetchResources();
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        setUploading(true);
        setUploadError('');

        const formData = new FormData();
        formData.append('title', titleRef.current.value);
        formData.append('grade_level', gradeRef.current.value);
        formData.append('subject', subjectRef.current.value);
        formData.append('file', fileRef.current.files[0]);

        try {
            const token = localStorage.getItem('auth_token');
            await axios.post(`${API_URL}/admin_upload_resource.php`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${token}`
                },
            });
            alert("Upload Successful");
            fetchResources();
            e.target.reset();
        } catch (err) {
            setUploadError(err.response?.data?.error || 'Upload failed');
        }
        setUploading(false);
    };

    const handleDeleteResource = async (resId) => {
        if (!window.confirm('Are you sure you want to delete this resource?')) return;
        try {
            const token = localStorage.getItem('auth_token');
            const formData = new FormData();
            formData.append('id', resId);
            await axios.post(`${API_URL}/delete_resource.php`, formData, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            fetchResources();
        } catch (err) {
            alert('Delete failed: ' + (err.response?.data?.error || err.message));
        }
    };

    return (
        <div className="resource-wrapper">

            {/* Header */}
            <div className="page-header">
                <div>
                    <h1>Resource Library</h1>
                    <p>Access study materials, past papers, and teacher guides.</p>
                </div>
            </div>

            {/* Admin Upload Section */}
            {userRole === 'developer' && (
                <div className="upload-card">
                    <div className="card-header-row">
                        <h3><Icons.Upload /> Admin Upload</h3>
                    </div>
                    <form onSubmit={handleUpload} className="upload-form">
                        <div className="form-group">
                            <label>Resource Title</label>
                            <input ref={titleRef} type="text" placeholder="e.g. 2024 Math Exam" required className="modern-input" />
                        </div>
                        <div className="form-group half">
                            <label>Grade</label>
                            <input ref={gradeRef} type="number" placeholder="1-12" required className="modern-input" />
                        </div>
                        <div className="form-group half">
                            <label>Subject</label>
                            <select ref={subjectRef} required defaultValue="" className="modern-select">
                                <option value="" disabled>Select Subject</option>
                                <option value="Mathematics">Mathematics</option>
                                <option value="English">English</option>
                                <option value="Afrikaans Home Language">Afrikaans Home Language</option>
                                <option value="Physical Sciences">Physical Sciences</option>
                                <option value="Life Sciences">Life Sciences</option>
                                <option value="Geography">Geography</option>
                                <option value="History">History</option>
                                <option value="Accounting">Accounting</option>
                                <option value="Business Studies">Business Studies</option>
                                <option value="Computer Applications Technology">CAT</option>
                                <option value="Information Technology">IT</option>
                                <option value="Life Orientation">Life Orientation</option>
                                <option value="Economics">Economics</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>
                        <div className="form-group full">
                            <label>File Attachment</label>
                            <input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.pptx" required className="file-input" />
                        </div>
                        <button type="submit" disabled={uploading} className="btn-primary full-width">
                            {uploading ? 'Uploading...' : 'Upload Resource'}
                        </button>
                    </form>
                    {uploadError && <div className="error-message">{uploadError}</div>}
                </div>
            )}

            {/* Filter Bar */}
            <div className="filter-bar">
                <form onSubmit={handleFilterSubmit} className="filter-form">
                    <div className="filter-group">
                        <input
                            type="number"
                            name="grade"
                            placeholder="Grade..."
                            value={filters.grade}
                            onChange={handleFilterChange}
                            className="modern-input small"
                        />
                    </div>
                    <div className="filter-group">
                        <select
                            name="subject"
                            value={filters.subject}
                            onChange={handleFilterChange}
                            className="modern-select small"
                        >
                            <option value="">All Subjects</option>
                            {Array.from(new Set(resources.map(r => r.subject))).sort().map(subject => (
                                <option key={subject} value={subject}>{subject}</option>
                            ))}
                        </select>
                    </div>
                    <button type="submit" className="btn-filter">
                        <Icons.Filter /> Filter
                    </button>
                </form>
                <span className="resource-count">{resources.length} Files Found</span>
            </div>

            {/* Resource Grid */}
            <div className="resource-grid">
                {resources.length === 0 ? (
                    <div className="empty-state">
                        <p>No resources found matching your criteria.</p>
                    </div>
                ) : (
                    Object.entries(
                        resources.reduce((groups, res) => {
                            const key = `${res.subject}||${res.grade_level}`;
                            if (!groups[key]) groups[key] = [];
                            groups[key].push(res);
                            return groups;
                        }, {})
                    ).map(([key, group]) => {
                        const [subject, grade] = key.split('||');
                        return (
                            <div key={key} className="resource-folder-card">
                                <div className="folder-header">
                                    <div className="folder-icon"><Icons.Folder /></div>
                                    <div className="folder-info">
                                        <h3>{subject}</h3>
                                        <span className="grade-badge">Grade {grade}</span>
                                    </div>
                                </div>
                                <div className="file-list">
                                    {group.map((res) => (
                                        <div key={res.id} className="file-item">
                                            <div className="file-icon"><Icons.File /></div>
                                            <div className="file-details">
                                                <span className="file-title" title={res.title}>{res.title}</span>
                                                <span className="file-date">{new Date(res.uploaded_at).toLocaleDateString()}</span>
                                            </div>
                                            <div className="file-actions">
                                                {res.file_path && (
                                                    <a
                                                        href={`https://marksure.tech/${res.file_path}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        download
                                                        className="action-btn download"
                                                        title="Download"
                                                    >
                                                        <Icons.Download />
                                                    </a>
                                                )}
                                                {userRole === 'developer' && (
                                                    <button
                                                        onClick={() => handleDeleteResource(res.id)}
                                                        className="action-btn delete"
                                                        title="Delete"
                                                    >
                                                        <Icons.Trash />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default ResourceBank;